import axios from 'axios'
import {useEffect, useState} from 'react'
import {imageBaseUrl} from 'src/cms/helpers/constants'
import {API_URL} from './CBS/api'

/**
 * Turns a stored upload path into a link the browser can actually open.
 *
 * VAPT ("Unauthenticated Access to Sensitive Uploaded Files"): files under /uploads are no longer
 * served off disk. The backend gates them behind an expiring HMAC (App\Helpers\SignedUploadUrl),
 * so `${imageBaseUrl}/${storedPath}` — which is what these screens used to build — now comes back
 * 401 for anything the media manager holds.
 *
 * The media manager's own listing already returns a signed `downloadLink` per file, so browsing
 * and downloading inside the file manager needs nothing from here. What does need this is every
 * screen that renders a path it saved EARLIER: FormInputMediaManager stores the media manager's
 * `storageLink` (a bare relative path, deliberately — an expiring URL must never be persisted),
 * and on the next page load there is no signed link to go with it.
 *
 * POST /api/documents/signed-url mints one on demand. It is authenticated: the caller is trusted
 * to mint access to the path it names, which is why this cannot be used for the pre-login logo or
 * favicon — those have no token to send and must stay publicly readable.
 */

/** A settings row as returned by /api/frontend/setting/type/{id}. */
export type SettingAssetRow = {
  value?: string
  valueUrl?: string | null
}

/**
 * URL for a settings row whose value names an uploaded file — the logo, the favicon, an icon.
 *
 * These are drawn on pages that have no token (the login screen, the splash, the favicon in the
 * tab), so they cannot fetch a link from the authenticated signing endpoint above. The backend
 * signs them for us instead and returns it as `valueUrl` alongside the raw `value`.
 *
 * Falls back to the old unsigned concatenation when `valueUrl` is absent, so the frontend keeps
 * working against an API that has not shipped it yet, and against settings that point at
 * something outside the protected tree.
 */
export const settingAssetUrl = (setting?: SettingAssetRow | null): string => {
  if (!setting) {
    return ''
  }

  if (setting.valueUrl) {
    return setting.valueUrl
  }

  const value = (setting.value ?? '').trim()

  return value ? `${imageBaseUrl}/${value}` : ''
}

/** Ask the backend for an expiring link to a stored path. */
export const fetchSignedMediaUrl = async (filePath: string, ttl?: number): Promise<string> => {
  const response = await axios.post(`${API_URL}/documents/signed-url`, {
    file_path: filePath,
    ...(ttl ? {ttl} : {}),
  })

  return response?.data?.data?.url ?? ''
}

/**
 * Signed URL for a stored path, refreshed whenever the path changes.
 *
 * Returns '' until the link arrives, so callers should treat empty as "nothing to show yet"
 * rather than rendering a broken <img>. An already-absolute value (an external DMS link, or a
 * `downloadLink` that was just handed to us by the picker) is passed straight through — it is
 * either already signed or not ours to sign.
 */
export const useSignedMediaUrl = (storedPath?: string | null, ttl?: number): string => {
  const [url, setUrl] = useState('')

  useEffect(() => {
    const path = (storedPath ?? '').trim()

    if (!path) {
      setUrl('')
      return
    }

    if (/^(https?:)?\/\//i.test(path)) {
      setUrl(path)
      return
    }

    // The path can change while a request is in flight (a user picking one image after another),
    // so a stale response must not overwrite a newer one.
    let active = true

    fetchSignedMediaUrl(path, ttl)
      .then((signed) => {
        if (active) setUrl(signed)
      })
      .catch(() => {
        // Leave it empty rather than falling back to an unsigned URL: that would render a broken
        // image and, worse, suggest the path is publicly readable when it is not.
        if (active) setUrl('')
      })

    return () => {
      active = false
    }
  }, [storedPath, ttl])

  return url
}
